import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { DisconnectReason, LocalVideoTrack, Participant, RemoteTrack, RemoteVideoTrack, Room, VideoTrack } from 'livekit-client';
import { patchApi, type PatchJoin, type PatchPhone, type PatchView, type VoicemailOffer } from '../api/client';
import { applyChosenDevices, chooseDevice, devicesChanged, onDeviceChosen, savedDevice, switchRoomDevice } from './audioDevices';
import { useMessagesLive } from './messages';
import { shareProblemText } from './patchText';

/**
 * Patches in the browser: one LiveKit room at a time, kept here (outside React) so the Patch stays
 * connected while the person moves around the app. The floating bar and the panel
 * (components/PatchDock.tsx) read it with usePatch(); the Messages page starts and joins Patches.
 * A microphone, a camera (when someone turns theirs on) and a shared screen are sent; sound plays
 * from hidden audio elements and video shows inside the Patch's own panel, its film strip or its
 * own window (lib/patchPip), all marked private, so live help and session recordings never include
 * a Patch. The speaker and microphone are the ones chosen on this browser for calls and Patches
 * alike (lib/audioDevices).
 *
 * LiveKit's browser SDK is loaded the first time someone patches in, not with the app.
 */

type LiveKit = typeof import('livekit-client');
let lk: LiveKit | null = null;
const sdk = async (): Promise<LiveKit> => (lk ??= await import('livekit-client'));

export interface PatchPerson {
  id: string;
  name: string;
  me: boolean;
  speaking: boolean;
  muted: boolean;
  sharing: boolean;
  /** Their camera is on. */
  camera: boolean;
}

export type PatchPhase = 'idle' | 'joining' | 'connected' | 'reconnecting' | 'ended';

export interface PatchState {
  phase: PatchPhase;
  tenant: string;
  conversationId: string;
  /** "#front-desk", or the other people in a direct message. */
  title: string;
  patchId: string;
  /** When the Patch started, on this device's clock. */
  startedAt: number;
  people: PatchPerson[];
  muted: boolean;
  /** Why the microphone can't be used, in plain words. They can still listen. */
  micProblem: string | null;
  /** A chosen speaker or microphone that isn't there any more, in words (the default is used). */
  deviceNotice: string | null;
  sharing: boolean;
  /** This browser can share its screen (desktop browsers; phones can only watch). */
  canShare: boolean;
  shareProblem: string | null;
  /** This person's camera is on; which way it faces (phones have two); why it couldn't start. */
  camera: boolean;
  facing: 'user' | 'environment';
  canFlip: boolean;
  cameraProblem: string | null;
  /** Whose camera is on (this person's too), in the order they're shown. */
  videos: { id: string; name: string; me: boolean }[];
  /** Who is sharing a screen now, and whose this person is looking at. */
  screens: { id: string; name: string }[];
  watching: string | null;
  /** The browser is holding the sound back until the person clicks something. */
  needsClick: boolean;
  /** The panel is open (otherwise the small bar, or the film strip while someone's video is on). */
  open: boolean;
  /**
   * The panel fills the window ("Full screen"): tiles as large as they fit, or a shared screen as
   * the stage with everyone beside it. Kept for this Patch, so it opens that way again.
   */
  expanded: boolean;
  /** Why it ended or couldn't start, when it wasn't their own choice. */
  notice: string | null;
  /** This person started the Patch: they may end it for everyone, not only leave. */
  starter: boolean;
  /** Who said no to it ringing them (a direct message's Patch): "Sam can't join right now". */
  declined: { id: string; name: string }[];
  /** Who a direct message's Patch rings: the others in it (none for a channel's). */
  rung: string[];
  /**
   * Desk phones on it (1.12.18): "Ringing Dana’s desk phone", "Dana · on desk phone", "Leaving a
   * voicemail for Dana". One answered is in the room too (its identity is desk-<their id>).
   */
  phones: PatchPhone[];
  /** A 1-on-1's other person who didn't come in (1.13.0): the Patch offers to leave them a voicemail. */
  voicemail: VoicemailOffer[];
  /** The microphone is being turned on or off (Mute waits: a tap then can't undo the one before). */
  micBusy: boolean;
}

const IDLE: PatchState = {
  phase: 'idle',
  tenant: '',
  conversationId: '',
  title: '',
  patchId: '',
  startedAt: 0,
  people: [],
  muted: false,
  micProblem: null,
  deviceNotice: null,
  sharing: false,
  canShare: false,
  shareProblem: null,
  camera: false,
  facing: 'user',
  canFlip: false,
  cameraProblem: null,
  videos: [],
  screens: [],
  watching: null,
  needsClick: false,
  open: false,
  expanded: false,
  notice: null,
  starter: false,
  declined: [],
  rung: [],
  phones: [],
  voicemail: [],
  micBusy: false
};

let state: PatchState = IDLE;
const listeners = new Set<() => void>();

function set(next: Partial<PatchState>): void {
  state = { ...state, ...next };
  for (const l of [...listeners]) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** The Patch this tab is in (or joining), for the bar, the panel and the Messages page. */
export function usePatch(): PatchState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

/** Someone this Patch rang said no: the people in it see so (until that person comes in after all). */
export function patchDeclined(tenant: string, conversationId: string, patchId: string, who: { id: string; name: string }): void {
  if (!inPatch(state, tenant, conversationId) || state.patchId !== patchId || state.declined.some((x) => x.id === who.id)) return;
  set({ declined: [...state.declined, { id: who.id, name: who.name }] });
}

/** Who's in changed (desk phones rung, answered or gone): the Patch this tab is in follows. */
export function patchPhones(tenant: string, conversationId: string, view: PatchView): void {
  if (!inPatch(state, tenant, conversationId) || (state.patchId && state.patchId !== view.id)) return;
  const phones = view.phones ?? [];
  const voicemail = view.voicemail ?? [];
  if (JSON.stringify(phones) === JSON.stringify(state.phones) && JSON.stringify(voicemail) === JSON.stringify(state.voicemail)) return;
  set({ phones, voicemail });
}

/** "Leave a voicemail" (1.13.0): calls their mailbox into this Patch. Null when it went, else why not. */
export async function leaveVoicemail(userId: string): Promise<string | null> {
  const { tenant, patchId } = state;
  if (!tenant || !patchId) return 'You’re not in a Patch.';
  try {
    const r = await patchApi.voicemail(tenant, patchId, userId);
    // Shown at once; the next update from Stitch confirms it.
    set({ voicemail: state.voicemail.filter((o) => o.userId !== userId), phones: [...state.phones.filter((p) => p.id !== r.phone.id), r.phone] });
    return null;
  } catch (err) {
    return err instanceof Error ? err.message : 'The voicemail couldn’t be started.';
  }
}

/** A desk phone in the room (desk-<user id>): what it is, for its tile and the list. */
export function deskPhoneOf(s: Pick<PatchState, 'phones'>, identity: string): PatchPhone | null {
  if (!identity.startsWith('desk-')) return null;
  const id = identity.slice(5);
  return s.phones.find((p) => p.userId === id && p.state === 'active') ?? null;
}

/** The Patch state now, and changes to it (outside React: lib/patchRing). */
export const patchNow = (): PatchState => state;
export const onPatchState = subscribe;

/** In a Patch (or on the way in) in this conversation. */
export const inPatch = (s: PatchState, tenant: string, conversationId: string) =>
  (s.phase === 'joining' || s.phase === 'connected' || s.phase === 'reconnecting') && s.tenant === tenant.toLowerCase() && s.conversationId === conversationId;

// --- the room -------------------------------------------------------------------------------------------

let room: Room | null = null;
/** Each "Patch in" gets a number; an answer for an older one is dropped. */
let ticket = 0;
let audioBox: HTMLDivElement | null = null;

/** Phones and tablets can watch a shared screen but can't share their own. */
export function canShareScreen(): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.getDisplayMedia !== 'function') return false;
  return !/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

/** Phones and tablets have a front and a back camera to switch between. */
function canFlipCamera(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function sounds(): HTMLDivElement {
  if (!audioBox || !audioBox.isConnected) {
    audioBox = document.createElement('div');
    audioBox.hidden = true;
    audioBox.setAttribute('aria-hidden', 'true');
    // Never copied into live help or a session recording.
    audioBox.setAttribute('data-private', '');
    audioBox.className = 'patch-sounds';
    document.body.appendChild(audioBox);
  }
  return audioBox;
}

function nameOf(p: Participant): string {
  return p.name || 'Someone';
}

/** Who's in, speaking, muted and sharing, from the room itself. */
function refresh(): void {
  const r = room;
  const L = lk;
  if (!r || !L) return;
  const { Track } = L;
  const all: Participant[] = [r.localParticipant, ...r.remoteParticipants.values()];
  const people = all.map((p) => ({
    id: p.identity,
    name: nameOf(p),
    me: p === r.localParticipant,
    speaking: p.isSpeaking,
    muted: !p.isMicrophoneEnabled,
    sharing: p.isScreenShareEnabled,
    camera: p.isCameraEnabled
  }));
  const screens = [...r.remoteParticipants.values()].filter((p) => Boolean(p.getTrackPublication(Track.Source.ScreenShare)?.videoTrack)).map((p) => ({ id: p.identity, name: nameOf(p) }));
  const watching = state.watching && screens.some((s) => s.id === state.watching) ? state.watching : (screens[0]?.id ?? null);
  // Cameras: the others first, then this person's own (a small picture of themselves).
  const showing = (p: Participant) => {
    const pub = p.getTrackPublication(Track.Source.Camera);
    return Boolean(pub?.videoTrack && !pub.isMuted);
  };
  const videos = [
    ...[...r.remoteParticipants.values()].filter(showing).map((p) => ({ id: p.identity, name: nameOf(p), me: false })),
    ...(showing(r.localParticipant) ? [{ id: r.localParticipant.identity, name: nameOf(r.localParticipant), me: true }] : [])
  ];
  set({
    people,
    screens,
    watching,
    videos,
    muted: !r.localParticipant.isMicrophoneEnabled,
    sharing: r.localParticipant.isScreenShareEnabled,
    camera: r.localParticipant.isCameraEnabled
  });
}

/** The video of someone's shared screen, to show in the panel. */
export function screenTrack(identity: string): RemoteVideoTrack | null {
  if (!lk) return null;
  const p = room?.remoteParticipants.get(identity);
  const t = p?.getTrackPublication(lk.Track.Source.ScreenShare)?.videoTrack;
  return (t as RemoteVideoTrack | undefined) ?? null;
}

/** Someone's camera (this person's own too), to show in the panel. */
export function cameraTrack(identity: string): VideoTrack | null {
  if (!lk || !room) return null;
  const p: Participant | undefined = room.localParticipant.identity === identity ? room.localParticipant : room.remoteParticipants.get(identity);
  return (p?.getTrackPublication(lk.Track.Source.Camera)?.videoTrack as VideoTrack | undefined) ?? null;
}

function cameraProblemText(err: unknown): string {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) return 'This page can’t use a camera: it needs a secure (https) connection.';
  if (!lk) return 'Your camera couldn’t start. Try again.';
  const { MediaDeviceFailure } = lk;
  switch (MediaDeviceFailure.getFailure(err)) {
    case MediaDeviceFailure.PermissionDenied:
      return 'Your browser is blocking the camera for Stitch. Allow it in the site settings (on a phone: the phone’s settings for Stitch), then try again.';
    case MediaDeviceFailure.NotFound:
      return 'No camera was found.';
    case MediaDeviceFailure.DeviceInUse:
      return 'Another app is using your camera. Close it, then try again.';
    default:
      return 'Your camera couldn’t start. Try again.';
  }
}

function micProblemText(err: unknown): string {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) return 'This page can’t use a microphone: it needs a secure (https) connection. You can still listen.';
  if (!lk) return 'Your microphone couldn’t start. Press Unmute to try again. You can still listen.';
  const { MediaDeviceFailure } = lk;
  switch (MediaDeviceFailure.getFailure(err)) {
    case MediaDeviceFailure.PermissionDenied:
      return 'Your browser is blocking the microphone for Stitch. Allow it in the site settings (by the address bar), then press Unmute. You can still listen.';
    case MediaDeviceFailure.NotFound:
      return 'No microphone was found. Plug one in, then press Unmute. You can still listen.';
    case MediaDeviceFailure.DeviceInUse:
      return 'Another app is using your microphone. Close it, then press Unmute. You can still listen.';
    default:
      return 'Your microphone couldn’t start. Press Unmute to try again. You can still listen.';
  }
}

/** The speaker and microphone chosen on this browser, once the microphone is allowed (so they can be listed). */
async function applyChosen(r: Room): Promise<void> {
  const gone = await applyChosenDevices(r).catch(() => null);
  if (room === r) set({ deviceNotice: gone });
}

/** Turns the microphone on or off. Turning it on can fail (blocked, missing, busy): they can still listen. */
async function setMic(on: boolean): Promise<void> {
  const r = room;
  if (!r) return;
  // The microphone chosen on this browser; one that's unplugged gets the browser's own (and the
  // device check after says so).
  const id = on ? savedDevice('audioinput') : '';
  set({ micBusy: true });
  try {
    await r.localParticipant.setMicrophoneEnabled(on, id ? { deviceId: id } : undefined);
    if (room === r) set({ micProblem: null });
    if (on) void applyChosen(r);
  } catch (err) {
    if (room === r) set({ micProblem: micProblemText(err) });
  } finally {
    if (room === r) set({ micBusy: false });
  }
  refresh();
}

// A speaker or microphone chosen during the Patch: its sound moves to it.
onDeviceChosen((kind, id) => {
  const r = room;
  if (!r) return;
  void switchRoomDevice(r, kind, id).then(
    () => room === r && set({ deviceNotice: null }),
    () => room === r && set({ micProblem: kind === 'audiooutput' ? 'That speaker couldn’t be used. Choose another.' : 'That microphone couldn’t be used. Choose another.' })
  );
});

function goodbye(reason: DisconnectReason | undefined): string | null {
  if (!lk) return null;
  const { DisconnectReason } = lk;
  switch (reason) {
    case DisconnectReason.CLIENT_INITIATED:
      return null;
    case DisconnectReason.DUPLICATE_IDENTITY:
      return 'You joined this Patch from another tab or device, so this one left it.';
    case DisconnectReason.ROOM_DELETED:
    case DisconnectReason.ROOM_CLOSED:
      return 'The Patch ended.';
    default:
      return 'The connection to the Patch was lost. Patch in again to rejoin.';
  }
}

function wire(r: Room, L: LiveKit): void {
  const { RoomEvent, Track } = L;
  const again = () => room === r && refresh();
  for (const e of [
    RoomEvent.ParticipantConnected,
    RoomEvent.ParticipantDisconnected,
    RoomEvent.TrackPublished,
    RoomEvent.TrackUnpublished,
    RoomEvent.TrackMuted,
    RoomEvent.TrackUnmuted,
    RoomEvent.LocalTrackPublished,
    RoomEvent.LocalTrackUnpublished,
    RoomEvent.ActiveSpeakersChanged,
    RoomEvent.ParticipantNameChanged
  ]) {
    r.on(e, again);
  }
  r.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
    if (track.kind === Track.Kind.Audio) sounds().appendChild(track.attach());
    again();
  });
  r.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
    for (const el of track.detach()) el.remove();
    again();
  });
  r.on(RoomEvent.AudioPlaybackStatusChanged, () => room === r && set({ needsClick: !r.canPlaybackAudio }));
  // A headset plugged in or out: the lists follow, and a chosen one that went falls back to the default.
  r.on(RoomEvent.MediaDevicesChanged, () => {
    if (room !== r) return;
    void devicesChanged(r).then((gone) => room === r && gone && set({ deviceNotice: gone }));
  });
  r.on(RoomEvent.MediaDevicesError, (err: Error) => room === r && set({ micProblem: micProblemText(err) }));
  r.on(RoomEvent.Reconnecting, () => room === r && set({ phase: 'reconnecting' }));
  r.on(RoomEvent.SignalReconnecting, () => room === r && set({ phase: 'reconnecting' }));
  r.on(RoomEvent.Reconnected, () => room === r && (set({ phase: 'connected' }), refresh()));
  r.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
    if (room !== r) return;
    const { tenant, patchId } = state;
    room = null;
    clearSounds();
    // Moved to another tab or device: still in the Patch there, so Stitch isn't told they left.
    if (tenant && patchId && reason !== L.DisconnectReason.DUPLICATE_IDENTITY) void patchApi.leave(tenant, patchId).catch(() => undefined);
    set({ ...IDLE, phase: 'ended', tenant, notice: goodbye(reason) ?? 'You left the Patch.' });
  });
}

function clearSounds(): void {
  if (audioBox) audioBox.replaceChildren();
}

/**
 * "Patch in": joins the conversation's Patch, or starts one. Already in it: opens the panel. In
 * another one: leaves that first.
 */
export async function patchIn(opts: { tenant: string; conversationId: string; title: string; rung?: string[] }): Promise<void> {
  const tenant = opts.tenant.toLowerCase();
  if (inPatch(state, tenant, opts.conversationId)) return set({ open: true });
  if (room || state.phase === 'joining') await leavePatch();
  const mine = ++ticket;
  set({ ...IDLE, phase: 'joining', tenant, conversationId: opts.conversationId, title: opts.title, open: true, canShare: canShareScreen(), canFlip: canFlipCamera() });
  // LiveKit's SDK first: if it can't load, nothing is started (and nobody is told).
  let L: LiveKit;
  try {
    L = await sdk();
  } catch {
    if (mine === ticket) set({ phase: 'ended', notice: 'The Patch couldn’t load. Check your connection, then Patch in again.' });
    return;
  }
  if (mine !== ticket) return;
  let join: PatchJoin;
  try {
    join = await patchApi.join(tenant, opts.conversationId);
  } catch (err) {
    if (mine === ticket) set({ phase: 'ended', notice: err instanceof Error ? err.message : 'The Patch couldn’t start.' });
    return;
  }
  if (mine !== ticket) {
    void patchApi.leave(tenant, join.patch.id).catch(() => undefined);
    return;
  }
  const offset = Date.parse(join.now) - Date.now();
  set({
    patchId: join.patch.id,
    starter: join.patch.startedBy.id === join.identity,
    rung: (opts.rung ?? []).filter((id) => id !== join.identity),
    startedAt: Date.parse(join.patch.startedAt) - offset,
    people: join.patch.people.map((p) => ({ id: p.id, name: p.name, me: p.id === join.identity, speaking: false, muted: true, sharing: false, camera: false })),
    phones: join.patch.phones ?? [],
    voicemail: join.patch.voicemail ?? []
  });
  const r = new L.Room({
    adaptiveStream: true,
    dynacast: true,
    disconnectOnPageLeave: true,
    audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    // Cameras at 720p in H.264 (sharper than VP8 at the same bitrate, and encoded in hardware on
    // most computers and phones), sent in layers so someone on a slow connection gets a smaller one.
    videoCaptureDefaults: { resolution: L.VideoPresets.h720.resolution },
    publishDefaults: {
      simulcast: true,
      videoCodec: 'h264',
      videoEncoding: { maxBitrate: 2_500_000, maxFramerate: 30 },
      videoSimulcastLayers: [L.VideoPresets.h360, L.VideoPresets.h180],
      // A shared screen: full detail at 15 frames a second, so text stays sharp.
      screenShareEncoding: L.ScreenSharePresets.h1080fps15.encoding
    }
  });
  room = r;
  wire(r, L);
  try {
    await r.connect(join.url, join.token);
  } catch {
    if (room === r) {
      room = null;
      r.removeAllListeners();
      void patchApi.leave(tenant, join.patch.id).catch(() => undefined);
      set({ phase: 'ended', notice: 'Couldn’t connect to the Patch. Check your connection, then Patch in again.' });
    }
    return;
  }
  if (room !== r) {
    void r.disconnect();
    return;
  }
  set({ phase: 'connected', needsClick: !r.canPlaybackAudio });
  refresh();
  await setMic(true);
}

/** Leaves the Patch (it ends when the last person leaves). */
export async function leavePatch(): Promise<void> {
  ticket++;
  const r = room;
  const { tenant, patchId } = state;
  room = null;
  set({ ...IDLE });
  clearSounds();
  if (r) {
    r.removeAllListeners();
    await r.disconnect().catch(() => undefined);
  }
  if (tenant && patchId) await patchApi.leave(tenant, patchId).catch(() => undefined);
}

/** The person who started the Patch ends it for everyone (they leave, and so does everyone else). */
export async function endPatch(): Promise<void> {
  const { tenant, patchId } = state;
  if (tenant && patchId) {
    try {
      await patchApi.end(tenant, patchId);
    } catch {
      // Couldn't end it for everyone: at least this person leaves.
    }
  }
  await leavePatch();
}

/** Mute or unmute, as the button showed: never while the microphone is still being turned on or off. */
export const toggleMute = () => (state.micBusy ? Promise.resolve() : setMic(state.muted));

/** Chooses the microphone or speaker (kept for Patches and calls on this browser). */
export function chooseAudio(kind: 'audioinput' | 'audiooutput', deviceId: string): void {
  chooseDevice(kind, deviceId);
}

export async function toggleShare(): Promise<void> {
  const r = room;
  if (!r || !lk) return;
  const L = lk;
  const on = !r.localParticipant.isScreenShareEnabled;
  set({ shareProblem: null });
  try {
    await r.localParticipant.setScreenShareEnabled(
      on,
      on
        ? {
            audio: true,
            selfBrowserSurface: 'exclude',
            surfaceSwitching: 'include',
            systemAudio: 'include',
            // Text and lines, not motion: the browser keeps it sharp and drops frames instead.
            contentHint: 'detail'
          }
        : undefined,
      on
        ? {
            // One layer, at full detail: a smaller copy for people on slow connections made the
            // picture jump between sharp and blurry as Stitch switched between them. And ahead of
            // the camera when the connection is short, with frames dropped before sharpness.
            simulcast: false,
            screenShareEncoding: { ...L.ScreenSharePresets.h1080fps15.encoding, priority: 'high' },
            degradationPreference: 'maintain-resolution'
          }
        : undefined
    );
  } catch (err) {
    if (room === r) set({ shareProblem: shareProblemText(err) });
  }
  refresh();
}


/** Turns this person's camera on or off (off by default: a Patch starts as a voice call). */
export async function toggleCamera(): Promise<void> {
  const r = room;
  if (!r) return;
  const on = !r.localParticipant.isCameraEnabled;
  set({ cameraProblem: null });
  try {
    await r.localParticipant.setCameraEnabled(on, on ? { facingMode: state.facing } : undefined);
  } catch (err) {
    if (room === r) set({ cameraProblem: cameraProblemText(err) });
  }
  refresh();
}

/** On a phone: the front camera or the back one. */
export async function flipCamera(): Promise<void> {
  const r = room;
  if (!r || !lk) return;
  const facing = state.facing === 'user' ? 'environment' : 'user';
  set({ facing });
  const track = r.localParticipant.getTrackPublication(lk.Track.Source.Camera)?.videoTrack as LocalVideoTrack | undefined;
  if (!track) return;
  try {
    await track.restartTrack({ facingMode: facing });
  } catch (err) {
    if (room === r) set({ cameraProblem: cameraProblemText(err) });
  }
  refresh();
}

/** In a Patch now (or joining one): a call in the app leaves it first. */
export const patchBusy = () => state.phase === 'joining' || state.phase === 'connected' || state.phase === 'reconnecting';

export const watchScreen = (identity: string) => set({ watching: identity });
export const openPanel = (open: boolean) => set({ open });
/** Full screen, or back to the panel's own size (for the rest of this Patch). */
export const setExpanded = (expanded: boolean) => state.expanded !== expanded && set({ expanded });
export const dismissNotice = () => state.phase === 'ended' && set({ ...IDLE });

/** The browser held the sound back (no click yet): this click lets it play. */
export async function startSound(): Promise<void> {
  await room?.startAudio().catch(() => undefined);
  set({ needsClick: Boolean(room && !room.canPlaybackAudio) });
}

if (typeof window !== 'undefined') {
  // Closing the tab: tell Stitch straight away (LiveKit hears it too).
  window.addEventListener('pagehide', () => {
    if (!room || !state.tenant || !state.patchId) return;
    void fetch(patchApi.leaveUrl(state.tenant, state.patchId), {
      method: 'POST',
      keepalive: true,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'x-stitch-request': '1' },
      body: '{}'
    }).catch(() => undefined);
  });
  // Signed out: out of the Patch too.
  window.addEventListener('stitch:signed-out', () => void leavePatch());
}

/** A clock that ticks once a second while `on`. */
export function useTicking(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [on]);
  return now;
}

// --- the Patches going in a company's conversations ----------------------------------------------------

interface Live {
  enabled: boolean | null;
  patches: Map<string, PatchView>;
  loadedAt: number;
}

const companies = new Map<string, Live>();
const liveListeners = new Set<() => void>();
let liveVersion = 0;

function touch(): void {
  liveVersion++;
  for (const l of [...liveListeners]) l();
}

const loading = new Map<string, Promise<void>>();

/** Loads a company's live Patches (one request at a time per company, however many ask). */
function loadLive(tenant: string): Promise<void> {
  const busy = loading.get(tenant);
  if (busy) return busy;
  const p = (async () => {
    try {
      const r = await patchApi.live(tenant);
      companies.set(tenant, { enabled: r.enabled, patches: new Map(r.patches.map((x) => [x.conversationId, x])), loadedAt: Date.now() });
    } catch {
      // Not available (Patches off, Stitch staff, or no access): nothing shows.
      companies.set(tenant, { enabled: false, patches: new Map(), loadedAt: Date.now() });
    } finally {
      loading.delete(tenant);
    }
    touch();
  })();
  loading.set(tenant, p);
  return p;
}

const isPatchView = (x: unknown): x is PatchView =>
  Boolean(x) && typeof (x as PatchView).id === 'string' && typeof (x as PatchView).conversationId === 'string' && Array.isArray((x as PatchView).people);

/**
 * Whether Patches are on in this company, and the Patch going in each conversation, kept up to
 * date live over Messages' stream.
 */
export function useLivePatches(tenant: string): { enabled: boolean; loaded: boolean; patchIn: (conversationId: string) => PatchView | null } {
  const key = tenant.toLowerCase();
  useSyncExternalStore(
    (l) => {
      liveListeners.add(l);
      return () => liveListeners.delete(l);
    },
    () => liveVersion,
    () => liveVersion
  );
  const asked = useRef('');
  useEffect(() => {
    const had = companies.get(key);
    if (asked.current === key && had) return;
    asked.current = key;
    if (!had || Date.now() - had.loadedAt > 15_000) void loadLive(key);
  }, [key]);
  useMessagesLive((e) => {
    if (e.type === 'reset') return void loadLive(key);
    if (e.type !== 'patch' || e.tenant !== key || !e.conversationId) return;
    const c = companies.get(key) ?? { enabled: true, patches: new Map<string, PatchView>(), loadedAt: Date.now() };
    if (isPatchView(e.patch)) c.patches.set(e.conversationId, e.patch);
    else c.patches.delete(e.conversationId);
    companies.set(key, c);
    touch();
  });
  const c = companies.get(key);
  return { enabled: Boolean(c?.enabled), loaded: Boolean(c), patchIn: (conversationId) => c?.patches.get(conversationId) ?? null };
}
